import Text from "common/Typography/Text";
import styled, { useTheme } from "styled-components";
import Flex from "common/Boxes/Flex";
import { useContext } from "react";
import { BotFormContext } from "@shared/features/bots/BotForm/common/BotForm.context";
import { motion } from "motion/react";
import { hexToRGB } from "@shared/utils/misc";
import { BotType, LanguageNameSpaces } from "@shared/types";
import useMedia from "utils/hooks/useMedia";
import { SMALL_MOBILE, SMALL_SCREEN } from "common/constants";
import { typography } from "@shared/common/Themes/typography";
import Button from "common/Buttons/Button.tsx";
import { BACKTESTING_VALUES_MODIFIED_MESSAGE } from "@shared/features/bots/BotForm/common/constants.ts";
import { useBottomNavigator } from "utils/hooks/misc.tsx";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { TriangleAlert } from "lucide-react";
import {
  getNewLookTokens,
  NEW_LOOK_RADIUS,
  newLookType,
} from "common/NewLook/tokens.ts";

const BacktestingValuesModifiedMessage = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const { isAdvancedView, initializeFormValues, backtestBotData, botType } =
    useContext(BotFormContext)!;
  const isSmallScreen = useMedia(
    [`(max-width: ${SMALL_SCREEN}`],
    [true],
    false
  );
  const isSmallMobile = useMedia(
    [`(max-width: ${SMALL_MOBILE}`],
    [true],
    false
  );
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const isVertical = botType !== BotType.SIGNAL || isSmallScreen;

  const theme = useTheme();
  const { isNewLook } = theme;
  let padding = isAdvancedView ? "4px 16px 20px" : "20px 16px 4px";
  if (isBottomNavigator) padding = "4px 12px 20px";
  if (isNewLook) padding = isBottomNavigator ? "0 0 12px" : "0";

  if (isNewLook) {
    // New look: a quiet notice in a well — the warning colour is on the icon
    // only — with a secondary "Revert changes" action.
    return (
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ ease: "easeInOut", duration: 0.3 }}
      >
        <div style={{ padding }}>
          <NewLookNotice role="status">
            <TriangleAlert
              size={16}
              strokeWidth={2}
              color={getNewLookTokens(theme).stop}
            />
            <NewLookText>{t(BACKTESTING_VALUES_MODIFIED_MESSAGE)}</NewLookText>
            <Button
              onClick={() => {
                initializeFormValues?.(backtestBotData);
              }}
            >
              {t("backtesting.buttons.revert_changes")}
            </Button>
          </NewLookNotice>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ ease: "easeInOut", duration: 0.3 }}
    >
      <Wrapper style={{ padding }}>
        <LeftBorder $isVertical={isVertical}>
          <ColoredText
            $size={
              isSmallMobile ? typography.fontSizes.S : typography.fontSizes.M
            }
          >
            {t(BACKTESTING_VALUES_MODIFIED_MESSAGE)}
          </ColoredText>
          <ColoredButton
            onClick={() => {
              initializeFormValues?.(backtestBotData);
            }}
            $isVertical={isVertical}
          >
            {t("backtesting.buttons.revert_changes")}
          </ColoredButton>
        </LeftBorder>
      </Wrapper>
    </motion.div>
  );
};

export default BacktestingValuesModifiedMessage;

const Wrapper = styled.div`
  padding: 16px;
`;
const LeftBorder = styled(Flex)<{ $isVertical?: boolean }>`
  border-left: 2px solid ${(p) => p.theme.colors.gold};
  ${(p) =>
    p.$isVertical &&
    `
    flex-direction: column;
    align-items: flex-start;
  `};
`;
const ColoredText = styled(Text)`
  color: ${(p) => p.theme.colors.gold};
  padding-left: 12px;
`;
const ColoredButton = styled(Button)<{ $isVertical?: boolean }>`
  margin-left: 12px;
  border-color: ${(p) => p.theme.colors.gold} !important;
  color: ${(p) => p.theme.colors.gold} !important;
  &.ant-btn-default:not(:disabled):not(.ant-btn-disabled):hover {
    color: ${(p) => hexToRGB(p.theme.colors.gold, 0.75)} !important;
    border-color: ${(p) => hexToRGB(p.theme.colors.gold, 0.75)} !important;
  }
  ${(p) =>
    p.$isVertical &&
    `
    margin-top: 8px;
  `}
`;
const NewLookNotice = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 12px;
  padding: 12px 16px;
  border-radius: ${NEW_LOOK_RADIUS.control}px;
  background: ${(p) => getNewLookTokens(p.theme).well};
  box-shadow: inset 0 0 0 1px ${(p) => getNewLookTokens(p.theme).hairline};
  > svg {
    flex: none;
  }
  .ant-btn {
    flex: none;
    margin-left: auto;
  }
`;
const NewLookText = styled.span`
  flex: 1 1 240px;
  min-width: 0;
  ${newLookType.body};
  color: ${(p) => getNewLookTokens(p.theme).text};
`;
